import React, { useState, useCallback } from 'react';
import { RoomCard } from './RoomCard';
import { Loading } from '../ui';
import { buildNocRooms } from '../../utils';

/**
 * Room selector view component
 */
export const RoomSelector = ({ rooms, onSelectRoom, sensorConfig }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const nocRooms = buildNocRooms(rooms);
  const connectedCount = nocRooms.filter((room) => room.hasData).length;
  const totalCount = nocRooms.length;

  const filteredRooms = searchQuery.trim()
    ? nocRooms.filter((room) => {
        const label = (room.displayName || room.code).toLowerCase();
        const zone = (room.zone || '').toLowerCase();
        const query = searchQuery.toLowerCase();
        return label.includes(query) || zone.includes(query);
      })
    : nocRooms;

  const handleClear = useCallback(() => setSearchQuery(''), []);

  return (
    <div className="rooms-container">
      <div className="rooms-header">
        <p className="rooms-subtitle">Selecciona una sala para ver los datos con mayor detalle</p>
      </div>

      <div className="rooms-stats">
        <div className="rooms-stat">
          <span className="rooms-stat-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="14" rx="2" />
              <path d="M7 20h10" />
              <path d="M9 8h6" />
              <path d="M8 12h8" />
            </svg>
          </span>
          <div>
            <div className="rooms-stat-value blue">{totalCount}</div>
            <div className="rooms-stat-label">Total Piscinas</div>
          </div>
        </div>
        <div className="rooms-stat">
          <span className="rooms-stat-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v7" />
              <path d="M7.8 5.8a7 7 0 1 0 8.4 0" />
            </svg>
          </span>
          <div>
            <div className="rooms-stat-value green">{connectedCount}</div>
            <div className="rooms-stat-label">Conectadas</div>
          </div>
        </div>
        <div className="rooms-stat">
          <span className="rooms-stat-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v7" />
              <path d="M7.8 5.8a7 7 0 1 0 8.4 0" />
              <path d="M4 4l16 16" />
            </svg>
          </span>
          <div>
            <div className="rooms-stat-value red">{totalCount - connectedCount}</div>
            <div className="rooms-stat-label">Sin Telemetria</div>
          </div>
        </div>
      </div>

      <div className="rooms-search-wrapper">
        <div className="rooms-search-bar">
          <span className="rooms-search-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </span>
          <input
            className="rooms-search-input"
            type="text"
            placeholder="Buscar piscina por nombre o zona…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
            spellCheck="false"
          />
          {searchQuery && (
            <button className="rooms-search-clear" onClick={handleClear} aria-label="Limpiar búsqueda">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          )}
        </div>
        {searchQuery.trim() && (
          <div className="rooms-search-meta">
            {filteredRooms.length > 0
              ? <><span className="rooms-search-count">{filteredRooms.length}</span> resultado{filteredRooms.length !== 1 ? 's' : ''} para "<em>{searchQuery.trim()}</em>"</>
              : <>Sin resultados para "<em>{searchQuery.trim()}</em>"</>
            }
          </div>
        )}
      </div>

      {filteredRooms.length > 0 ? (
        <div className="rooms-grid">
          {filteredRooms.map((room) => (
            <RoomCard key={room.code} room={room} onSelect={onSelectRoom} sensorConfig={sensorConfig} />
          ))}
        </div>
      ) : searchQuery.trim() ? (
        <div className="rooms-empty-state">
          <div className="rooms-empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
              <path d="M8 11h6" />
            </svg>
          </div>
          <p className="rooms-empty-title">No se encontraron piscinas</p>
          <p className="rooms-empty-hint">Intenta con otro nombre o zona</p>
          <button className="rooms-empty-reset" onClick={handleClear}>Limpiar búsqueda</button>
        </div>
      ) : null}

      {Object.values(rooms).length === 0 && (
        <Loading text="Esperando datos de sensores..." />
      )}
    </div>
  );
};
